/*
 * OSO instrument moments — scroll effects for the full-screen moment blocks.
 *
 * expand: the OSO Supreme v1 "expanding media" timing. Progress runs 0→1 while
 *   the frame's top moves from mid-viewport to 10% above it, and drives
 *   --om-inset (10% → 0 each side, a clip-path so no layout per frame),
 *   --om-radius (6px → 0), --om-scale (1 → 1.15) and --om-name (overlay
 *   title fades in over progress 0.35 → 0.85).
 * circle: the stage is pinned (sticky, --om-pin tall). Progress is how far the
 *   stage has scrolled through its pin; it drives --om-r (a gong-sized circle →
 *   the frame's half-diagonal), --om-scale (1.15 → 1) and --om-name (overlay
 *   title fades in over the last part).
 *
 * Progress is eased toward its scroll target each frame (lerp), so a mouse-wheel
 * step glides instead of jumping. The pin itself stays native scroll.
 *
 * Desktop fine-pointer only and off under prefers-reduced-motion; `.is-animating`
 * is added here only, so without JS every moment is the plain image.
 *
 * Phones (≤749px, every moment regardless of effect): a one-shot reveal.
 * `.m-reveal` hides the name and clips the image; `.is-revealed` is added once
 * the moment enters the viewport, and the CSS transitions do the rest.
 */
(function () {
  if (window.customElements.get('oso-moment')) return;

  var clamp = function (v) { return v < 0 ? 0 : v > 1 ? 1 : v; };
  var sine = function (p) { return 0.5 - Math.cos(p * Math.PI) / 2; };
  var SMOOTH = 0.14; // share of the remaining distance covered per frame

  class OsoMoment extends HTMLElement {
    connectedCallback() {
      this.effect = this.dataset.effect;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.setupReveal();
      if (this.effect !== 'expand' && this.effect !== 'circle') return;
      this.media = this.querySelector('.oso-moment__media');
      this.stage = this.querySelector('.oso-moment__stage');
      this.mq = window.matchMedia('(min-width: 900px) and (pointer: fine)');
      this.ticking = false;
      this.running = false;
      this.current = null;
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);
      this.setup = this.setup.bind(this);
      this.mq.addEventListener('change', this.setup);
      this.reduce.addEventListener('change', this.setup);
      this.setup();
    }

    setupReveal() {
      if (this.reduce.matches || !window.matchMedia('(max-width: 749px)').matches) return;
      if (!('IntersectionObserver' in window)) return;
      this.classList.add('m-reveal');
      this.io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        this.classList.add('is-revealed');
        this.io.disconnect();
      }.bind(this), { rootMargin: '0px 0px -15% 0px' });
      this.io.observe(this);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (!this.mq) return;
      this.stop();
      this.mq.removeEventListener('change', this.setup);
      this.reduce.removeEventListener('change', this.setup);
    }

    setup() {
      if (this.mq.matches && !this.reduce.matches) this.start();
      else this.stop();
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.classList.add('is-animating');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.update();
    }

    stop() {
      if (!this.running) return;
      this.running = false;
      this.classList.remove('is-animating');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.current = null;
      ['--om-inset', '--om-radius', '--om-scale', '--om-r', '--om-name', '--om-pin'].forEach(function (p) {
        this.style.removeProperty(p);
      }, this);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(this.update);
    }

    update() {
      this.ticking = false;
      var vh = window.innerHeight;
      var target;
      if (this.effect === 'expand') {
        target = clamp((vh * 0.5 - this.media.getBoundingClientRect().top) / (vh * 0.6));
      } else {
        this.pin = vh * 2.5;
        this.style.setProperty('--om-pin', this.pin + 'px');
        target = clamp(-this.stage.getBoundingClientRect().top / (this.pin - vh));
      }
      // First frame (and resizes) snap; scroll steps glide.
      if (this.current === null) this.current = target;
      else this.current += (target - this.current) * SMOOTH;
      if (Math.abs(target - this.current) < 0.0005) this.current = target;
      this.render(this.current, vh);
      if (this.current !== target) this.onScroll();
    }

    render(raw, vh) {
      if (this.effect === 'expand') {
        var p = sine(raw);
        this.style.setProperty('--om-inset', (10 * (1 - p)).toFixed(3) + '%');
        this.style.setProperty('--om-radius', (6 * (1 - p)).toFixed(2) + 'px');
        this.style.setProperty('--om-scale', (1 + 0.15 * p).toFixed(4));
        this.style.setProperty('--om-name', clamp((raw - 0.35) / 0.5).toFixed(3));
        return;
      }
      var open = sine(clamp(raw / 0.85));
      var w = this.media.clientWidth;
      var startR = Math.min(w, vh) * 0.24;
      var endR = Math.sqrt(w * w + vh * vh) / 2 + 2;
      this.style.setProperty('--om-r', (startR + (endR - startR) * open).toFixed(1) + 'px');
      this.style.setProperty('--om-scale', (1.15 - 0.15 * open).toFixed(4));
      this.style.setProperty('--om-name', clamp((raw - 0.6) / 0.3).toFixed(3));
    }
  }

  window.customElements.define('oso-moment', OsoMoment);
})();
